import Vue from 'vue';
import budgetRequest from '../../libs/ads-request/budget-request';
import QuotationRules from '../../libs/business-logic/Quotation.rules';
import ActivitiesRules from '../../libs/business-logic/Activities.rules';

export default {
  namespaced: true,

  state: {
    frozenQuotation: null,
    orderActual: null,
    orderPrincipal: null,
    ordersGrouped: null,
  },

  getters: {
    getQuotationItems(state) {
      return state.frozenQuotation;
    },

    getClientChargedQuotationItems(state) {
      return state.frozenQuotation.filter(
        (quotationItem) => quotationItem.activity_service_type === 1,
      );
    },

    getOtherChargesTotalAmount(state) {
      if (state.frozenQuotation.some(
        (quotationItem) => quotationItem.accepted
          && quotationItem.activity_service_type === 1
          && quotationItem.other_charges_type_id === null,
      )
      ) {
        return QuotationRules
          .getClientChargedOtherChargesSubtotal(state.frozenQuotation).toFixed(2);
      }

      return 0;
    },

    getOtherChargesSubtotal(state) {
      if (state.frozenQuotation.some(
        (quotationItem) => quotationItem.accepted && quotationItem.activity_service_type === 1,
      )
      ) {
        return QuotationRules.getClientChargedOtherChargesSubtotal(state.frozenQuotation);
      }

      return 0;
    },

    getRejectedActivities(state) {
      const groupedItemsByActivity = QuotationRules
        .groupQuotationItemsByActivity(state.frozenQuotation);
      const quotationItems = ActivitiesRules
        .getActivitiesFromGroupedQuotationItems(groupedItemsByActivity);
      const rejectedItems = ActivitiesRules.getRejectedActivities(quotationItems);

      return rejectedItems.map(
        (quotationItem) => ({
          title: quotationItem.title,
          description: quotationItem.description,
          activity_service_type: quotationItem.activity_service_type,
          activity_status_id: quotationItem.activity_status_id,
          order_activity_id: quotationItem.order_activity_id,
        }),
      );
    },

    getOrderActual(state) {
      return state.orderActual;
    },

    getOrderPrincipal(state) {
      return state.orderPrincipal;
    },
  },

  mutations: {
    SET_FROZEN_QUOTATION(state, payload) {
      Vue.set(state, 'frozenQuotation', payload);
    },

    UPDATE_ACCEPTED_QUOTATION_ITEMS(state, payload) {
      const frozenQuotationWithAcceptedValues = QuotationRules
        .assignAcceptedValuesToFrozenQuotation(state.frozenQuotation, payload);

      Vue.set(state, 'frozenQuotation', frozenQuotationWithAcceptedValues);
    },

    /**
     * Update 'accepted' value in frozen quotation items that belongs to a given activity.
     * @param {Object} state - The Quotation module state.
     * @param {Object} payload - The mutation payload.
     * @param {number} payload.activityId - The identifier of the changed activity.
     * @param {boolean} payload.acceptedValue - The new authorization value.
     */
    UPDATE_AUTHORIZATION_SELECTION(state, { activityId, acceptedValue }) {
      const itemsToUpdate = state.frozenQuotation.filter(
        (quotationItem) => quotationItem.order_activity_id === activityId,
      );

      itemsToUpdate.forEach((quotationItem) => {
        const indexOfItemToUpdate = state.frozenQuotation.indexOf(quotationItem);
        Vue.set(state.frozenQuotation[indexOfItemToUpdate], 'accepted', acceptedValue);
      });
    },
    SET_ORDER_ACTUAL(state, payload) {
      Vue.set(state, 'orderActual', payload);
    },
    SET_ORDER_PRINCIPAL(state, payload) {
      Vue.set(state, 'orderPrincipal', payload);
    },
  },

  actions: {
    async getFrozenQuotation({ commit }) {
      try {
        const quotationItems = await budgetRequest.getFrozenQuotation();
        const quotationItemsNoCanceled = QuotationRules.getQuotationItemOrdersNoCanceled(
          quotationItems,
        );
        commit('SET_FROZEN_QUOTATION', quotationItemsNoCanceled);
        commit('SET_ORDER_ACTUAL', QuotationRules.getOrderActual());
        return quotationItemsNoCanceled;
      } catch (err) {
        throw new Error('Ocurrió un error al obtener presupuestos congelados.');
      }
    },

    freezeQuotation: async () => {
      try {
        return await budgetRequest.freezeQuotation();
      } catch (err) {
        throw new Error('Ocurrió un error al congelar el presupuesto.');
      }
    },

    getOriginalQuotation: async ({ commit }) => {
      try {
        const originalQuotationItems = await budgetRequest.getAdditionalActivities(Vue.prototype.$session.get('orderId'));
        commit('UPDATE_ACCEPTED_QUOTATION_ITEMS', originalQuotationItems);
      } catch (err) {
        throw new Error('Ocurrió un error al obtener presupuesto.');
      }
    },
  },
};
